<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveUser">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Edit user') }}</h1>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <button
                            class="btn btn-red shadow-sm rounded-md"
                            type="button"
                            @click="deleteUserModal = true"
                        >
                            {{ $t('Delete user') }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('User details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('This information will be displayed publicly') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="name"
                                            v-model="user.name"
                                            :placeholder="$t('Name')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="email">{{ $t('Email') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="email"
                                            v-model="user.email"
                                            :placeholder="$t('Email')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                            type="email"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="password">{{ $t('New password') }}</label>
                                    <div class="mt-1 flex rounded-md shadow-sm">
                                        <input
                                            id="password"
                                            v-model="user.password"
                                            :placeholder="$t('Leave empty to keep the current password')"
                                            :type="showPassword ? 'text' : 'password'"
                                            autocomplete="new-password"
                                            class="form-input block w-full rounded-r-none transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            maxlength="255"
                                            minlength="6"
                                        >
                                        <button
                                            class="btn btn-secondary rounded-none border-l-0 whitespace-no-wrap"
                                            type="button"
                                            @click="showPassword = !showPassword"
                                        >
                                            {{ showPassword ? $t('Hide password') : $t('Show password') }}
                                        </button>
                                        <button
                                            class="btn btn-blue rounded-l-none whitespace-no-wrap"
                                            type="button"
                                            @click="generatePassword"
                                        >
                                            {{ $t('Generate password') }}
                                        </button>
                                    </div>
                                    <p class="mt-2 text-xs text-gray-500">
                                        {{ $t('Leave empty to keep the current password') }}.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="md:col-span-3">
                            <div class="py-3">
                                <div class="border-t border-gray-200"></div>
                            </div>
                        </div>
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('User settings') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('User access and permission settings') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="role">{{ $t('Role') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <select
                                            id="role"
                                            v-model="user.role_id"
                                            class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                            <option :value="null" disabled>{{ $t('Select an option') }}</option>
                                            <option v-for="userRole in userRoles" :value="userRole.id">{{ userRole.name }}</option>
                                        </select>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="status">{{ $t('Status') }}</label>
                                    <div class="mt-1 flex rounded-md">
                                        <input-switch
                                            id="status"
                                            v-model="user.status"
                                            :disabled-label="$t('The user is disabled')"
                                            :enabled-label="$t('The user is activated')"
                                        ></input-switch>
                                    </div>
                                    <div class="mt-2 relative text-xs text-gray-500">
                                        {{ $t('When the user is deactivated, the registry is created in the system, so the email is reserved, but can not login until it is activated again') }}.
                                    </div>
                                </div>
                                <!-- Frontend for Client/Partner -->
                                <div v-if="isClientOrPartner" class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="frontend_id">{{ $t('Frontend') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <select
                                            id="frontend_id"
                                            v-model="user.frontend_id"
                                            class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        >
                                            <option :value="null">{{ $t('None') }}</option>
                                            <option v-for="frontend in frontendList" :value="frontend.id">{{ frontend.name }}</option>
                                        </select>
                                    </div>
                                </div>
                                <!-- Frontends for Employee (multiple) -->
                                <div v-if="isEmployee" class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('Assigned frontends') }}</label>
                                    <div class="mt-1">
                                        <div v-for="frontend in frontendList" class="flex items-center mb-1">
                                            <input
                                                :id="'frontend-' + frontend.id"
                                                :value="frontend.id"
                                                v-model="user.frontend_ids"
                                                type="checkbox"
                                                class="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                                            >
                                            <label :for="'frontend-' + frontend.id" class="ml-2 text-sm text-gray-700">
                                                {{ frontend.name }}
                                            </label>
                                        </div>
                                    </div>
                                    <p class="mt-1 text-xs text-gray-500">
                                        {{ $t('Employee will see tickets, products and clients from selected frontends') }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <span class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                to="/dashboard/admin/users"
                            >
                                {{ $t('Close') }}
                            </router-link>
                            <button
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                            >
                                {{ $t('Edit user') }}
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </form>
        <div v-show="deleteUserModal" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <transition
                    duration="300"
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div v-show="deleteUserModal" class="fixed inset-0 transition-opacity">
                        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                    </div>
                </transition>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="deleteUserModal"
                        aria-labelledby="modal-headline"
                        aria-modal="true"
                        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                        role="dialog"
                    >
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <svg-vue class="h-6 w-6 pb-1 text-red-600" icon="font-awesome.exclamation-triangle-light"></svg-vue>
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 id="modal-headline" class="text-lg leading-6 font-medium text-gray-900">
                                        {{ $t('Delete user') }}
                                    </h3>
                                    <div class="mt-2">
                                        <p class="text-sm leading-5 text-gray-500">
                                            {{ $t('Are you sure you want to delete the user?') }}
                                            {{ $t('All data will be permanently removed') }}.
                                            {{ $t('This action cannot be undone') }}.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                class="btn btn-red mr-2 sm:mr-0"
                                type="button"
                                @click="deleteUser"
                            >
                                {{ $t('Delete user') }}
                            </button>
                            <button
                                class="btn btn-white mr-0 sm:mr-2"
                                type="button"
                                @click="deleteUserModal = false"
                            >
                                {{ $t('Cancel') }}
                            </button>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </main>
</template>

<script>
const ROLE_EMPLOYEE = 2;
const ROLE_PARTNER = 3;
const ROLE_CLIENT = 4;

export default {
    name: "edit-user",
    metaInfo() {
        return {
            title: this.$i18n.t('Edit user')
        }
    },
    data() {
        return {
            loading: true,
            deleteUserModal: false,
            showPassword: false,
            userRoles: [],
            frontendList: [],
            user: {
                name: null,
                email: null,
                role_id: null,
                frontend_id: null,
                frontend_ids: [],
                status: true,
                password: null,
            },
        }
    },
    computed: {
        isEmployee() {
            return this.user.role_id === ROLE_EMPLOYEE;
        },
        isClientOrPartner() {
            return this.user.role_id === ROLE_PARTNER || this.user.role_id === ROLE_CLIENT;
        },
    },
    mounted() {
        this.getUserRoles();
        this.getFrontends();
        this.getUser();
    },
    methods: {
        saveUser() {
            const self = this;
            self.loading = true;
            axios.put('api/dashboard/admin/users/' + self.$route.params.id, self.user).then(function () {
                self.loading = false;
                self.user.password = null;
                self.showPassword = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
            });
        },
        generatePassword() {
            const groups = [
                'ABCDEFGHJKLMNPQRSTUVWXYZ',
                'abcdefghijkmnopqrstuvwxyz',
                '23456789',
            ];
            const allCharacters = groups.join('');
            const randomIndex = (length) => {
                const randomValue = new Uint32Array(1);
                window.crypto.getRandomValues(randomValue);
                return randomValue[0] % length;
            };
            const password = groups.map(group => group[randomIndex(group.length)]);

            while (password.length < 16) {
                password.push(allCharacters[randomIndex(allCharacters.length)]);
            }
            for (let index = password.length - 1; index > 0; index--) {
                const swapIndex = randomIndex(index + 1);
                [password[index], password[swapIndex]] = [password[swapIndex], password[index]];
            }

            this.user.password = password.join('');
            this.showPassword = true;
        },
        getUser() {
            const self = this;
            self.loading = true;
            axios.get('api/dashboard/admin/users/' + self.$route.params.id).then(function (response) {
                response.data.password = null;
                self.user = response.data;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        deleteUser() {
            const self = this;
            axios.delete('api/dashboard/admin/users/' + self.$route.params.id).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/admin/users');
            }).catch(function () {
                self.deleteUserModal = false;
            });
        },
        getUserRoles() {
            const self = this;
            axios.get('api/dashboard/admin/users/user-roles').then(function (response) {
                self.userRoles = response.data;
            }).catch(function () {
                self.loading = false;
            });
        },
        getFrontends() {
            const self = this;
            axios.get('api/dashboard/admin/users/frontends').then(function (response) {
                self.frontendList = response.data;
            }).catch(function () {});
        },
    }
}
</script>
